import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { Subscription } from 'rxjs';
import { UserSettingsService, ThemeKey } from '../../../services/user-settings/user-settings.service';

type AnimationMode = 'gameOfLife' | 'frost' | null;

// ─── Game of Life cell (redFire) ─────────────────────────────────────
interface Cell {
  alive: boolean;
  opacity: number;
  targetOpacity: number;
  seeded: number;
  seedOpacity: number;
  seedTarget: number;
}

// ─── Frost crystal (blueIce) ─────────────────────────────────────────
interface FrostBranch {
  x1: number;
  y1: number;
  targetX: number;
  targetY: number;
  growth: number;        // 0..1; branch endpoint = lerp(start → target, growth)
  growthRate: number;    // amount added to growth per tick
  depth: number;         // 0 = main spine, 1 = fork, 2 = needle
  width: number;
  hasSpawned: boolean;   // whether this branch has emitted its child forks
}

interface FrostCrystal {
  branches: FrostBranch[];
  alpha: number;
  state: 'growing' | 'mature' | 'fading';
  maturityTicks: number;
  matureHoldFor: number; // randomised hold before fade-out begins
}

// ─── Rolling fog blob (blueIce, bottom of canvas) ────────────────────
interface FogBlob {
  x: number;
  y: number;
  baseY: number;        // center of vertical oscillation
  yPhase: number;
  ySpeed: number;       // rad/ms
  yAmplitude: number;
  baseRadiusX: number;
  baseRadiusY: number;
  radiusPhase: number;
  radiusSpeed: number;  // rad/ms
  radiusPulse: number;  // ±fraction modulation on radius (e.g. 0.12 = ±12%)
  vx: number;           // horizontal velocity, px/ms
  alpha: number;        // peak alpha for this blob
  alphaPhase: number;
  alphaSpeed: number;   // rad/ms
}

@Component({
  selector: 'app-animated-bg',
  template: '<canvas #canvas></canvas>',
  styleUrls: ['./animated-bg.component.scss']
})
export class AnimatedBgComponent implements AfterViewInit, OnDestroy {

  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @HostBinding('style.display') hostDisplay = 'block';

  private ctx!: CanvasRenderingContext2D;
  private tickTimer: any;
  private frameId = 0;
  private lastTime = 0;
  private running = false;
  private viewReady = false;
  private themeSubscription: Subscription;

  // Driven by theme: which animation is currently active (null = none).
  private mode: AnimationMode = null;

  // ─── Game of Life state ──────────────────────────────────────────────
  private golCells: Cell[][] = [];
  private golCols = 0;
  private golRows = 0;
  private readonly golCellSize = 32;
  private readonly golGap = 5;
  private golTickCount = 0;
  private golNextSeedTick = 3 + Math.floor(Math.random() * 4);
  private readonly golLerpRate = 0.0012;
  private readonly golPalette = { live: 'rgba(140, 20, 20, ', seed: 'rgba(230, 120, 30, ' };

  // ─── Frost state ─────────────────────────────────────────────────────
  private frostCrystals: FrostCrystal[] = [];
  private readonly FROST_MAX_CRYSTALS = 22;
  private readonly FROST_SPAWN_CHANCE = 0.4;     // per tick, when below cap
  private readonly FROST_DEPTH_LIMIT = 2;
  // Multiplier on per-tick branch extension. Lower = slower growth. 0.25 = 75% slower than the base rate.
  private readonly FROST_GROWTH_SCALE = 0.25;
  // Branches at each depth use a different cool tone (matches the three Blue Ice accents).
  private readonly FROST_COLORS: ReadonlyArray<readonly [number, number, number]> = [
    [200, 230, 255],  // depth 0: frost white-blue (main spines)
    [103, 232, 249],  // depth 1: frost cyan (forks)
    [94, 234, 212],   // depth 2: teal mint (needles)
  ];

  // ─── Fog state (lower portion of canvas during blueIce) ─────────────
  private fogBlobs: FogBlob[] = [];
  private fogOffscreen: HTMLCanvasElement | null = null;
  private readonly FOG_REGION = 0.25;            // fraction of canvas occupied by the dense fog zone
  private readonly FOG_FEATHER_PX = 90;          // vertical feather above the dense zone for a soft transition
  private readonly FOG_BLUR_PX = 9;              // blur applied to underlying crystals in the fog region
  private readonly FOG_BLOB_BLUR_PX = 28;        // additional softness on the fog blobs themselves
  private readonly FOG_BLOB_COUNT = 10;

  constructor(private userSettingsService: UserSettingsService) {
    const initialTheme = userSettingsService.getTheme();
    this.mode = this.modeForTheme(initialTheme);
    this.hostDisplay = this.mode ? 'block' : 'none';
  }

  ngAfterViewInit() {
    this.viewReady = true;

    if (this.mode) {
      this.initCanvas();
    }

    this.themeSubscription = this.userSettingsService.themeChangedObservable().subscribe(t => {
      const newMode = this.modeForTheme(t);
      if (newMode === this.mode && this.running) return;
      this.stopAnimation();
      this.mode = newMode;
      if (this.mode) {
        this.hostDisplay = 'block';
        // Allow a frame for the host to become visible and gain dimensions
        requestAnimationFrame(() => this.initCanvas());
      } else {
        this.hostDisplay = 'none';
      }
    });
  }

  ngOnDestroy() {
    this.stopAnimation();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private modeForTheme(theme: ThemeKey): AnimationMode {
    if (theme === 'redFire') return 'gameOfLife';
    if (theme === 'blueIce') return 'frost';
    return null;
  }

  private initCanvas() {
    if (!this.viewReady || !this.mode) return;

    // Stop any existing animation before reinitializing
    this.stopAnimation();

    const canvas = this.canvasRef.nativeElement;
    const parent = canvas.parentElement!;
    canvas.width = parent.offsetWidth;
    canvas.height = parent.offsetHeight;

    if (canvas.width === 0 || canvas.height === 0) return;

    this.ctx = canvas.getContext('2d')!;

    let tickIntervalMs: number;
    if (this.mode === 'gameOfLife') {
      this.initGameOfLife();
      tickIntervalMs = 1400;
    } else {
      this.initFrost();
      tickIntervalMs = 90;
    }

    this.running = true;
    this.tickTimer = setInterval(() => this.tick(), tickIntervalMs);
    this.lastTime = performance.now();
    this.frameId = requestAnimationFrame(this.render);
  }

  private stopAnimation() {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
    this.running = false;
  }

  private tick() {
    if (this.mode === 'gameOfLife') this.tickGameOfLife();
    else if (this.mode === 'frost') this.tickFrost();
  }

  private render = () => {
    const now = performance.now();
    const dt = now - this.lastTime;
    this.lastTime = now;

    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (this.mode === 'gameOfLife') this.renderGameOfLife(dt);
    else if (this.mode === 'frost') this.renderFrost(dt);

    this.frameId = requestAnimationFrame(this.render);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Game of Life (redFire)
  // ═══════════════════════════════════════════════════════════════════════

  private initGameOfLife() {
    const canvas = this.canvasRef.nativeElement;
    this.golCols = Math.ceil(canvas.width / (this.golCellSize + this.golGap)) + 1;
    this.golRows = Math.ceil(canvas.height / (this.golCellSize + this.golGap)) + 1;
    this.golCells = [];
    this.golTickCount = 0;
    this.golNextSeedTick = 3 + Math.floor(Math.random() * 4);

    for (let r = 0; r < this.golRows; r++) {
      this.golCells[r] = [];
      for (let c = 0; c < this.golCols; c++) {
        const alive = Math.random() < 0.12;
        this.golCells[r][c] = {
          alive,
          opacity: alive ? 1 : 0,
          targetOpacity: alive ? 1 : 0,
          seeded: 0,
          seedOpacity: 0,
          seedTarget: 0,
        };
      }
    }
  }

  private tickGameOfLife() {
    this.golTickCount++;

    if (this.golTickCount >= this.golNextSeedTick) {
      const count = 4 + Math.floor(Math.random() * 50);
      for (let i = 0; i < count; i++) {
        const r = Math.floor(Math.pow(Math.random(), 0.4) * this.golRows);
        const c = Math.floor(Math.random() * this.golCols);
        if (!this.golCells[r][c].alive) {
          this.golCells[r][c].alive = true;
          this.golCells[r][c].targetOpacity = 1;
          this.golCells[r][c].seeded = 3;
          this.golCells[r][c].seedTarget = 1;
        }
      }
      this.golNextSeedTick = this.golTickCount + 3 + Math.floor(Math.random() * 5);
    }

    const next: boolean[][] = [];
    for (let r = 0; r < this.golRows; r++) {
      next[r] = [];
      for (let c = 0; c < this.golCols; c++) {
        const n = this.countGolNeighbors(r, c);
        next[r][c] = this.golCells[r][c].alive ? (n === 2 || n === 3) : (n === 3);
      }
    }

    for (let r = 0; r < this.golRows; r++) {
      for (let c = 0; c < this.golCols; c++) {
        const cell = this.golCells[r][c];
        cell.alive = next[r][c];
        cell.targetOpacity = cell.alive ? 1 : 0;
        if (cell.seeded > 0) {
          cell.seeded--;
          if (cell.seeded === 0) {
            cell.seedTarget = 0;
          }
        }
      }
    }
  }

  private countGolNeighbors(row: number, col: number): number {
    let count = 0;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const r = row + dr;
        const c = col + dc;
        if (r >= 0 && r < this.golRows && c >= 0 && c < this.golCols && this.golCells[r][c].alive) {
          count++;
        }
      }
    }
    return count;
  }

  private renderGameOfLife(dt: number) {
    const ctx = this.ctx;
    const lerp = Math.min(1, this.golLerpRate * dt);
    const step = this.golCellSize + this.golGap;
    const r6 = 6;

    for (let r = 0; r < this.golRows; r++) {
      for (let c = 0; c < this.golCols; c++) {
        const cell = this.golCells[r][c];

        cell.opacity += (cell.targetOpacity - cell.opacity) * lerp;
        cell.seedOpacity += (cell.seedTarget - cell.seedOpacity) * lerp;

        const x = c * step;
        const y = r * step;

        if (cell.opacity > 0.01) {
          ctx.fillStyle = `${this.golPalette.live}${cell.opacity * 0.35})`;
          this.fillRoundRect(ctx, x, y, this.golCellSize, this.golCellSize, r6);
        }

        if (cell.seedOpacity > 0.01) {
          ctx.fillStyle = `${this.golPalette.seed}${cell.seedOpacity * 0.5})`;
          this.fillRoundRect(ctx, x, y, this.golCellSize, this.golCellSize, r6);
        }
      }
    }
  }

  private fillRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fill();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Frost (blueIce) — dendritic crystal growth with 6-fold hex symmetry.
  // Each crystal seeds 6 main spines at 60° intervals (the hexagonal habit
  // of real ice). Spines extend over several ticks, then fork at ±60°; forks
  // recurse one more level to short needles. Crystals mature, hold, then fade.
  // ═══════════════════════════════════════════════════════════════════════

  private initFrost() {
    this.frostCrystals = [];
    // Seed a handful of crystals at different growth stages so the canvas
    // doesn't start empty when the theme switches in.
    const initial = 5 + Math.floor(Math.random() * 4);
    for (let i = 0; i < initial; i++) {
      const crystal = this.seedCrystal();
      // Advance some by a few "virtual" ticks so they show various maturity.
      const head = Math.floor(Math.random() * 10);
      for (let h = 0; h < head; h++) {
        this.advanceCrystal(crystal);
      }
      this.frostCrystals.push(crystal);
    }
    this.initFog();
  }

  private initFog() {
    const canvas = this.canvasRef.nativeElement;
    this.fogBlobs = [];
    for (let i = 0; i < this.FOG_BLOB_COUNT; i++) {
      const blob = this.makeFogBlob();
      // Stagger initial positions across the full width instead of all entering from the left.
      blob.x = (i / this.FOG_BLOB_COUNT) * canvas.width + (Math.random() - 0.5) * 140;
      this.fogBlobs.push(blob);
    }

    // Offscreen buffer used to produce a feathered blur of the bottom region.
    if (!this.fogOffscreen) {
      this.fogOffscreen = document.createElement('canvas');
    }
    this.fogOffscreen.width = canvas.width;
    this.fogOffscreen.height = canvas.height;
  }

  // Creates a new fog blob with randomised motion / size parameters. Used both
  // at init and to refresh blobs after they drift off the right edge.
  private makeFogBlob(): FogBlob {
    const canvas = this.canvasRef.nativeElement;
    const fogCenter = canvas.height * (1 - this.FOG_REGION);
    // Centers can sit slightly ABOVE the dense zone so blobs naturally bleed
    // through the feathered transition into the sharper crystal field above.
    const minY = fogCenter - 40;
    const maxY = canvas.height + 20;
    const baseY = minY + Math.random() * (maxY - minY);
    const baseRadiusX = 200 + Math.random() * 320;
    const baseRadiusY = 60 + Math.random() * 90;
    return {
      x: -baseRadiusX - 30,
      y: baseY,
      baseY,
      yPhase: Math.random() * Math.PI * 2,
      ySpeed: 0.0003 + Math.random() * 0.0006,
      yAmplitude: 8 + Math.random() * 22,
      baseRadiusX,
      baseRadiusY,
      radiusPhase: Math.random() * Math.PI * 2,
      radiusSpeed: 0.0002 + Math.random() * 0.0005,
      radiusPulse: 0.10 + Math.random() * 0.14,
      // Wider velocity range than before — a few drift slowly, others sweep across.
      vx: 0.008 + Math.random() * 0.052,
      // Wider alpha range too, so density varies blob-to-blob.
      alpha: 0.10 + Math.random() * 0.26,
      alphaPhase: Math.random() * Math.PI * 2,
      alphaSpeed: 0.0003 + Math.random() * 0.0009,
    };
  }

  private seedCrystal(): FrostCrystal {
    const canvas = this.canvasRef.nativeElement;
    const cx = Math.random() * canvas.width;
    const cy = Math.random() * canvas.height;
    // Randomised rotation so adjacent crystals don't all align to the same axes.
    const angleOffset = Math.random() * Math.PI / 3;
    const armCount = 6;
    const spineLen = 38 + Math.random() * 55;
    const branches: FrostBranch[] = [];
    for (let i = 0; i < armCount; i++) {
      const angle = angleOffset + (i * Math.PI * 2 / armCount);
      branches.push({
        x1: cx, y1: cy,
        targetX: cx + Math.cos(angle) * spineLen,
        targetY: cy + Math.sin(angle) * spineLen,
        growth: 0,
        growthRate: (0.06 + Math.random() * 0.05) * this.FROST_GROWTH_SCALE,
        depth: 0,
        width: 1.3 + Math.random() * 0.4,
        hasSpawned: false,
      });
    }
    return {
      branches,
      alpha: 0,
      state: 'growing',
      maturityTicks: 0,
      matureHoldFor: 40 + Math.floor(Math.random() * 50),
    };
  }

  private tickFrost() {
    for (const crystal of this.frostCrystals) {
      this.advanceCrystal(crystal);
    }

    // Cull crystals that have completely faded out.
    this.frostCrystals = this.frostCrystals.filter(c => c.alpha > 0 || c.state === 'growing');

    // Spawn new crystals to keep the field populated.
    if (this.frostCrystals.length < this.FROST_MAX_CRYSTALS && Math.random() < this.FROST_SPAWN_CHANCE) {
      this.frostCrystals.push(this.seedCrystal());
    }
  }

  private advanceCrystal(crystal: FrostCrystal) {
    if (crystal.state === 'growing') {
      let anyStillGrowing = false;
      const spawned: FrostBranch[] = [];

      for (const branch of crystal.branches) {
        if (branch.growth < 1) {
          branch.growth = Math.min(1, branch.growth + branch.growthRate);
          anyStillGrowing = true;
        }
        // When a branch just finished growing, spit out its children.
        if (branch.growth >= 1 && !branch.hasSpawned && branch.depth < this.FROST_DEPTH_LIMIT) {
          branch.hasSpawned = true;
          spawned.push(...this.spawnForks(branch));
          anyStillGrowing = true;
        }
      }

      if (spawned.length) {
        crystal.branches.push(...spawned);
      }

      // Smooth fade-in during the growth phase.
      crystal.alpha = Math.min(1, crystal.alpha + 0.05);

      if (!anyStillGrowing) {
        crystal.state = 'mature';
      }
    } else if (crystal.state === 'mature') {
      crystal.maturityTicks++;
      if (crystal.maturityTicks >= crystal.matureHoldFor) {
        crystal.state = 'fading';
      }
    } else {
      // fading
      crystal.alpha = Math.max(0, crystal.alpha - 0.013);
    }
  }

  private spawnForks(parent: FrostBranch): FrostBranch[] {
    const px = parent.targetX - parent.x1;
    const py = parent.targetY - parent.y1;
    const parentAngle = Math.atan2(py, px);
    const parentLen = Math.hypot(px, py);

    // Main spines spawn 3–5 forks; forks spawn 1–2 shorter needles.
    const forkCount = parent.depth === 0
      ? 3 + Math.floor(Math.random() * 3)
      : 1 + Math.floor(Math.random() * 2);

    const out: FrostBranch[] = [];
    for (let f = 0; f < forkCount; f++) {
      // Origin somewhere along the parent (skip the very base + tip).
      const t = 0.25 + Math.random() * 0.65;
      const ox = parent.x1 + px * t;
      const oy = parent.y1 + py * t;

      // ±60° from the parent direction — preserves hex symmetry.
      const sign = Math.random() < 0.5 ? -1 : 1;
      const forkAngle = parentAngle + sign * Math.PI / 3;

      // Forks taper aggressively in length and width with depth.
      const lenFactor = parent.depth === 0
        ? 0.35 + Math.random() * 0.4
        : 0.3 + Math.random() * 0.3;
      const forkLen = parentLen * lenFactor;

      out.push({
        x1: ox, y1: oy,
        targetX: ox + Math.cos(forkAngle) * forkLen,
        targetY: oy + Math.sin(forkAngle) * forkLen,
        growth: 0,
        growthRate: (0.09 + Math.random() * 0.07) * this.FROST_GROWTH_SCALE,
        depth: parent.depth + 1,
        width: Math.max(0.5, parent.width * 0.68),
        hasSpawned: false,
      });
    }
    return out;
  }

  private renderFrost(dt: number) {
    const ctx = this.ctx;
    // Sharp, angular look — no rounded caps or joins.
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';

    for (const crystal of this.frostCrystals) {
      if (crystal.alpha <= 0.005) continue;

      for (const branch of crystal.branches) {
        if (branch.growth <= 0) continue;

        const x2 = branch.x1 + (branch.targetX - branch.x1) * branch.growth;
        const y2 = branch.y1 + (branch.targetY - branch.y1) * branch.growth;

        const colorIdx = Math.min(branch.depth, this.FROST_COLORS.length - 1);
        const [r, g, b] = this.FROST_COLORS[colorIdx];

        // Depth 0 reads brightest; deeper branches recede slightly so the
        // viewer's eye picks out the main hexagonal skeleton first.
        const depthAlpha = branch.depth === 0 ? 0.78 : (branch.depth === 1 ? 0.62 : 0.5);
        const alpha = crystal.alpha * depthAlpha;

        // Faint diffuse glow under the sharp line, then the crisp stroke on top.
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.18})`;
        ctx.lineWidth = branch.width + 2.2;
        ctx.beginPath();
        ctx.moveTo(branch.x1, branch.y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.lineWidth = branch.width;
        ctx.beginPath();
        ctx.moveTo(branch.x1, branch.y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }

    // Fog overlay (blur + cold haze + rolling fog) covers the bottom slice.
    this.renderFog(dt);
  }

  // Bottom-of-canvas fog: blurs the underlying frost with a feathered top
  // edge (so the transition is smooth, not a hard rectangle), lays down a
  // cold haze, then drifts soft fog blobs across the lower portion.
  private renderFog(dt: number) {
    const ctx = this.ctx;
    const canvas = this.canvasRef.nativeElement;
    const fogCenter = Math.floor(canvas.height * (1 - this.FOG_REGION));
    const featherTop = Math.max(0, fogCenter - this.FOG_FEATHER_PX);
    if (fogCenter >= canvas.height) return;

    // 1) Feathered blur pass — render the *whole* main canvas blurred onto an
    //    offscreen buffer, then mask it with a vertical alpha gradient so the
    //    blurred image only shows up below `featherTop`, fading in smoothly
    //    until it's fully opaque around `fogCenter`. This produces a soft
    //    transition between the sharp crystal field and the blurred fog zone
    //    instead of a visible horizontal seam.
    const off = this.fogOffscreen;
    if (off && off.width === canvas.width && off.height === canvas.height) {
      const offCtx = off.getContext('2d')!;
      offCtx.save();
      offCtx.globalCompositeOperation = 'copy';   // wipe + replace in one draw
      offCtx.filter = `blur(${this.FOG_BLUR_PX}px)`;
      offCtx.drawImage(canvas, 0, 0);
      offCtx.restore();

      // Mask: keep only the lower portion, fading in from featherTop → fogCenter.
      offCtx.save();
      offCtx.globalCompositeOperation = 'destination-in';
      const mask = offCtx.createLinearGradient(0, featherTop, 0, fogCenter);
      mask.addColorStop(0, 'rgba(0, 0, 0, 0)');
      mask.addColorStop(1, 'rgba(0, 0, 0, 1)');
      offCtx.fillStyle = mask;
      offCtx.fillRect(0, featherTop, canvas.width, canvas.height - featherTop);
      offCtx.restore();

      ctx.drawImage(off, 0, 0);
    }

    // 2) Cold haze base — vertical gradient seamlessly extends the feathered
    //    transition: 0 alpha at `featherTop`, growing toward the canvas bottom.
    const haze = ctx.createLinearGradient(0, featherTop, 0, canvas.height);
    haze.addColorStop(0, 'rgba(180, 220, 250, 0)');
    haze.addColorStop(0.35, 'rgba(180, 220, 250, 0.05)');
    haze.addColorStop(0.75, 'rgba(175, 215, 245, 0.12)');
    haze.addColorStop(1, 'rgba(165, 205, 240, 0.20)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, featherTop, canvas.width, canvas.height - featherTop);

    // 3) Rolling fog blobs — advance position, vertical drift, breathing alpha,
    //    and a slow radius pulse so the fog feels alive. No hard clip: blobs
    //    are free to bleed upward through the feather, blending with the
    //    blurred zone above.
    ctx.save();
    ctx.filter = `blur(${this.FOG_BLOB_BLUR_PX}px)`;

    for (const blob of this.fogBlobs) {
      blob.x += blob.vx * dt;
      blob.yPhase += blob.ySpeed * dt;
      blob.radiusPhase += blob.radiusSpeed * dt;
      blob.alphaPhase += blob.alphaSpeed * dt;

      // Vertical oscillation around the blob's baseline so it sways gently.
      blob.y = blob.baseY + Math.sin(blob.yPhase) * blob.yAmplitude;

      // Slow size pulse — fog "breathes" wider and narrower.
      const pulse = 1 + blob.radiusPulse * Math.sin(blob.radiusPhase);
      const rx = blob.baseRadiusX * pulse;
      const ry = blob.baseRadiusY * pulse;

      // Once the blob has fully drifted past the right edge, recycle it with
      // fresh randomised parameters entering from the left.
      if (blob.x - rx > canvas.width) {
        Object.assign(blob, this.makeFogBlob());
        continue;
      }

      // Alpha breathing: 50%–100% of peak — slightly wider than before.
      const breathing = 0.5 + 0.5 * (0.5 + 0.5 * Math.sin(blob.alphaPhase));
      const a = blob.alpha * breathing;

      const grad = ctx.createRadialGradient(
        blob.x, blob.y, 0,
        blob.x, blob.y, Math.max(rx, ry),
      );
      grad.addColorStop(0, `rgba(225, 240, 255, ${a})`);
      grad.addColorStop(0.55, `rgba(200, 225, 245, ${a * 0.45})`);
      grad.addColorStop(1, 'rgba(200, 225, 245, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(blob.x, blob.y, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
